// @ts-nocheck
/* eslint-disable func-names */
import { takeLatest, spawn, put, select } from 'redux-saga/effects';

import { parsedObjects } from 'utils/reduxActions';
import apiRequest, { apiSuccess, objectToQueryString } from '../../../utils/api';
import { truthty } from '../../../utils/functions';
import { appActions, PRINT_RECEPTION_LABEL } from '../../../commons/reducer';
import { RECEIPTS } from '../reducer';
import { checkAutomation } from 'utils/hooks/useAutomationPermission';

function* getStateFromApi() {
  yield takeLatest([RECEIPTS.GET_STATE_FROM_API, RECEIPTS.CHANGE_PAGINATION], function* (action) {
    yield put(appActions.setLoader('filter-receipts', true));
    const response = yield apiRequest(
      `api/v1/receipts?${objectToQueryString({
        page: action.pagination.page,
        rowsPerPage: action.pagination.rowsPerPage,
        ...action.filters,
      })}`,
      { method: 'get' },
    );
    yield put(
      apiSuccess(RECEIPTS.GET_STATE_FROM_API_SUCCESS, response, { pagination: action.pagination }),
    );
    yield put(appActions.setLoader('filter-receipts', false));
  });
}

function* createReceipt() {
  yield takeLatest(RECEIPTS.CREATE_RECEIPT, function* (action) {
    yield put(appActions.setLoader('create-edit-receipt', true));
    const permissions = yield select((state) => state.app.permissions);
    const hasAutomation = checkAutomation(permissions);

    const receiptPayload = {
      date: action.controls.date,
      harvestDate: action.controls.harvestDate,
      lotNumber: action.controls.lotNumber,
      supplierId: action.controls.supplierId,
      harvester: action.controls.harvester,
      dispatchOrderNumber: action.controls.dispatchOrderNumber,
      paddockId: action.controls.paddockId,
      price: action.controls.price,
      clientId: action.controls.clientId,
      ...(hasAutomation
        ? {
            fruitId: action.controls.fruitId,
            varietyId: action.controls.varietyId,
            productId: action.controls.productId,
            warehouseId: action.controls.warehouseId || null,
            packagingTypeId: action.controls.packagingTypeId,
            packagingQuantity: action.controls.packagingQuantity,
            quantity: action.controls.quantity,
            hasPallet: action.controls.hasPallet,
            splitPaks: action.controls.multipleLots,
            customField: action.controls.customField,
            customFields: {
              deliveryType: action.controls.deliveryType || '',
              supplierName: action.controls.supplierName || '',
              callOff: action.controls.callOff || '',
              incomingDeliveryReference:
                action.controls.incomingDeliveryReference || '',
              plateNumber: action.controls.plateNumber || '',
            },
          }
        : {}),
    };

    const response = yield apiRequest('api/v1/receipts', {
      method: 'post',
      body: JSON.stringify(receiptPayload),
    });

    if (truthty(response.error)) {
      yield put(appActions.setError(response.errorMsg));
    } else {
      yield put(apiSuccess(RECEIPTS.CREATE_RECEIPT_SUCCESS, response));
    }
    yield put(appActions.setLoader('create-edit-receipt', false));
  });
}

function* createReceiptLots() {
  yield takeLatest(RECEIPTS.CREATE_RECEIPT_LOTS, function* (action) {
    yield put(appActions.setLoader('create-receipt-lots', true));

    const response = yield apiRequest('api/v1/receipts/receiptLots', {
      method: 'post',
      body: JSON.stringify({
        lots: action.lots,
        receiptId: action.receiptId,
      }),
    });
    if (truthty(response.error)) {
      yield put(appActions.setError(response.errorMsg));
    } else {
      yield put(apiSuccess(RECEIPTS.CREATE_RECEIPT_LOTS_SUCCESS, response));

      if (action.shouldAutoPrint && truthty(response.objects.lots)) {
        const getObjects = (state) => state.app.objects;
        const objects = yield parsedObjects(yield select(getObjects));

        yield put({
          type: PRINT_RECEPTION_LABEL,
          device: action.device,
          allowPdfFallback: false,
          labelData: {
            lotIds: Object.values(response.objects.lots).map((l) => l.id),
            accountId: Object.values(objects.accounts)[0]?.id,
          },
        });
      }
    }
    yield put(appActions.setLoader('create-receipt-lots', false));
  });
}

function* editReceipt() {
  yield takeLatest(RECEIPTS.EDIT_RECEIPT, function* (action) {
    yield put(appActions.setLoader('create-edit-receipt', true));
    try {
      const {
        deliveryType,
        callOff,
        incomingDeliveryReference,
        plateNumber,
        supplierName,
        ...rest
      } = action.controls;
      const permissions = yield select((state) => state.app.permissions);
      const hasAutomation = checkAutomation(permissions);
      const path = hasAutomation
        ? `api/v1/receipts/${action.id}/automation`
        : `api/v1/receipts/${action.id}`;

      const response = yield apiRequest(path, {
        method: 'put',
        body: JSON.stringify({
          ...rest,
          splitPaks: action.controls.multipleLots,
          ...(hasAutomation && {
            customFields: {
              deliveryType,
              callOff,
              incomingDeliveryReference,
              plateNumber,
              supplierName,
            },
          }),
        }),
      });

      if (truthty(response.error)) {
        yield put(appActions.setError(response.error));
      } else {
        yield put(apiSuccess(RECEIPTS.EDIT_RECEIPT_SUCCESS, response));
        yield put(appActions.setSuccess('apiSuccess.receiptUpdated'));
      }
    } catch (e) {
      yield put(appActions.setError('apiError.networkError'));
    }
    yield put(appActions.setLoader('create-edit-receipt', false));
  });
}

function* editReceiptData() {
  yield takeLatest(RECEIPTS.EDIT_RECEIPT_DATA, function* (action) {
    yield put(appActions.setLoader('create-edit-receipt-data', true));
    try {
      const permissions = yield select((state) => state.app.permissions);
      const hasAutomation = checkAutomation(permissions);
      const path = hasAutomation
        ? `api/v1/receipts/${action.id}/automation-data`
        : `api/v1/receipts/${action.id}/data`;

      const response = yield apiRequest(path, {
        method: 'put',
        body: JSON.stringify(action.controls),
      });

      if (truthty(response.error)) {
        yield put(appActions.setError(response.errorMsg));
      } else {
        yield put(apiSuccess(RECEIPTS.EDIT_RECEIPT_DATA_SUCCESS, response));
      }
    } catch (e) {
      yield put(appActions.setError(e.message));
    }
    yield put(appActions.setLoader('create-edit-receipt-data', false));
  });
}

function* deleteReceipt() {
  yield takeLatest(RECEIPTS.DELETE_RECEIPT, function* (action) {
    yield put(appActions.setLoader('delete-receipt', true));
    const response = yield apiRequest(`api/v1/receipts/${action.id}`, { method: 'delete' });
    if (truthty(response.error)) {
      yield put(appActions.setError(response.errorMsg));
    } else {
      yield put(apiSuccess(RECEIPTS.DELETE_RECEIPT_SUCCESS, { item: response, type: 'receipts' }));
    }
    yield put(appActions.setLoader('delete-receipt', false));
  });
}

function* deleteLot() {
  yield takeLatest(RECEIPTS.DELETE_LOT, function* (action) {
    yield put(appActions.setLoader('delete-lot', true));
    const response = yield apiRequest(`api/v1/lots/${action.id}`, { method: 'delete' });
    const { lotToDelete } = response;
    yield put(apiSuccess(RECEIPTS.DELETE_LOT_SUCCESS, { item: lotToDelete, type: 'lots' }));
    yield put(appActions.setLoader('delete-lot', false));
  });
}

export default function* saga() {
  yield spawn(createReceiptLots);
  yield spawn(getStateFromApi);
  yield spawn(deleteReceipt);
  yield spawn(createReceipt);
  yield spawn(editReceipt);
  yield spawn(editReceiptData);
  yield spawn(deleteLot);
}
